import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import styles from "./approvals.module.css";
import { approvalsService, type PendingApproval } from "./approvalsService";

import { Badge } from "@/components/Badge";
import { PageHeader } from "@/components/PageHeader";
import manualStyles from "@/features/manual/manual.module.css";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
}

/** The directors' queue: everything waiting for approval. */
export function ApprovalsPage() {
  const [pending, setPending] = useState<PendingApproval[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    approvalsService
      .listPending()
      .then((r) => setPending(r.pending))
      .catch(() => setError(true));
  }, []);

  return (
    <div className={`${manualStyles.view} ${manualStyles.viewWide}`}>
      <PageHeader
        eyebrow="Approvals"
        title="Waiting for your approval"
        intro="Nothing goes live until it’s approved here. Rejecting sends it back to the editor with your comment."
      />

      {error && <div className={manualStyles.emptyState}>The queue couldn&rsquo;t be loaded.</div>}
      {!pending && !error && <div className={manualStyles.quiet}>Loading…</div>}
      {pending && pending.length === 0 && (
        <div className={manualStyles.emptyState}>
          Nothing waiting. New submissions from the editors land here.
        </div>
      )}

      {(pending ?? []).map((item) => (
        <Link key={item.versionId} to={`/approvals/${item.versionId}`} className={styles.row}>
          <div className={styles.rowTop}>
            <span className={styles.rowTitle}>{item.title}</span>
            <Badge tone={item.isNewPage ? "success" : "warning"} className={styles.kindTag}>
              {item.isNewPage ? "New page" : "Change to published page"}
            </Badge>
          </div>
          <div className={styles.rowMeta}>
            {item.sectionTitle} · v{item.versionNo} · submitted by {item.authorName ?? "—"} ·{" "}
            {formatDate(item.submittedAt)}
            {item.required > 1 ? ` · ${item.approvedSoFar}/${item.required} approvals` : ""}
          </div>
          {item.changeSummary && <div className={styles.rowNote}>“{item.changeSummary}”</div>}
        </Link>
      ))}
    </div>
  );
}
